<script lang="ts">
	import type { Issue, Report } from '#lib/types';
	import { i18n, t } from '#lib/i18n/state.svelte';
	import { excerpt } from '#lib/text/normalize';
	import { confidenceLabel } from '#lib/ui/labels';

	interface Props {
		report: Report;
		onjump: (issue: Issue) => void;
	}
	let { report, onjump }: Props = $props();

	let view = $state<'matches' | 'orphans' | 'missing'>('matches');

	const r = $derived(report.citations);
	const issueFor = (citationRaw: string): Issue | undefined =>
		report.issues.find(
			(i) => i.category === 'citations' && i.quote && citationRaw.length > 0 && i.quote.includes(citationRaw)
		);

	const missing = $derived(r.matches.filter((m) => m.status === 'missing'));
	const mismatch = $derived(r.matches.filter((m) => m.status === 'year-mismatch' || m.status === 'author-mismatch'));

	function authorLabel(authors: string[]): string {
		if (authors.length === 0) return '?';
		if (authors.length === 1) return authors[0];
		if (authors.length === 2) return `${authors[0]} & ${authors[1]}`;
		return `${authors[0]} ${t('refs.etal')}`;
	}
</script>

<div class="refs-tab">
	<div class="tiles">
		<div class="tile">
			<span class="n">{r.summary.citations}</span><span class="l">{t('refs.citations')}</span>
		</div>
		<div class="tile">
			<span class="n">{r.summary.references}</span><span class="l">{t('refs.references')}</span>
		</div>
		<div class="tile">
			<span class="n" style:color="#3f6212">{r.summary.matched}</span><span class="l">{t('refs.matched')}</span>
		</div>
		<div class="tile">
			<span class="n" style:color="#b3261e">{r.summary.missing}</span><span class="l">{t('refs.missing')}</span>
		</div>
		<div class="tile">
			<span class="n" style:color="#9a5b06">{r.summary.orphans}</span><span class="l">{t('refs.orphans')}</span>
		</div>
	</div>

	{#if !r.sectionDetected}
		<div class="notice">
			<strong>{t('refs.noSectionTitle')}</strong>
			{t('refs.noSectionBody')}
			{#if r.summary.citations > 0}
				{t('refs.noSectionCitations', { n: r.summary.citations })}
			{/if}
		</div>
	{/if}

	<div class="seg" role="tablist" aria-label={t('refs.viewAria')}>
		<button
			class="seg-btn"
			class:active={view === 'matches'}
			onclick={() => (view = 'matches')}
			role="tab"
			aria-selected={view === 'matches'}
		>
			{t('refs.tab.matches')} <span class="count">{r.matches.length}</span>
		</button>
		<button
			class="seg-btn"
			class:active={view === 'missing'}
			onclick={() => (view = 'missing')}
			role="tab"
			aria-selected={view === 'missing'}
		>
			{t('refs.tab.missing')} <span class="count">{missing.length + mismatch.length}</span>
		</button>
		<button
			class="seg-btn"
			class:active={view === 'orphans'}
			onclick={() => (view = 'orphans')}
			role="tab"
			aria-selected={view === 'orphans'}
		>
			{t('refs.tab.orphans')} <span class="count">{r.orphans.length}</span>
		</button>
	</div>

	{#if view === 'matches'}
		{#if r.matches.length === 0}
			<p class="empty muted">{t('refs.emptyMatches')}</p>
		{:else}
			<div class="card table-wrap">
				<table>
					<thead>
						<tr>
							<th scope="col">{t('refs.col.citation')}</th>
							<th scope="col">{t('refs.col.status')}</th>
							<th scope="col">{t('refs.col.match')}</th>
						</tr>
					</thead>
					<tbody>
						{#each r.matches as m (m.citation.id)}
							<tr>
								<td class="mono">{m.citation.raw}</td>
								<td>
									{#if m.status === 'matched'}
										<span class="chip chip-ok">{t('refs.status.matched')}</span>
									{:else if m.status === 'missing'}
										<span class="chip chip-critical">{t('refs.status.missing')}</span>
									{:else if m.status === 'year-mismatch'}
										<span class="chip chip-warning">{t('refs.status.year')}</span>
									{:else}
										<span class="chip chip-warning">{t('refs.status.author')}</span>
									{/if}
								</td>
								<td class="match-cell">
									{#if m.matched}
										<span class="mono">{authorLabel(m.matched.authors)}, {m.matched.year ?? '—'}</span>
										<span class="raw-ref">{excerpt(m.matched.raw, 90)}</span>
									{:else}
										<span class="muted">—</span>
									{/if}
								</td>
							</tr>
						{/each}
					</tbody>
				</table>
			</div>
		{/if}
	{/if}

	{#if view === 'missing'}
		{#if missing.length + mismatch.length === 0}
			<p class="empty muted">{t('refs.emptyMissing')}</p>
		{:else}
			<ul class="issue-list">
				{#each [...missing, ...mismatch] as m (m.citation.id)}
					<li class="mini card" class:critical={m.status === 'missing'}>
						<div class="mini-head">
							<span class="mono">{m.citation.raw}</span>
							{#if m.status === 'missing'}
								<span class="chip chip-critical">{t('refs.status.missing')}</span>
							{:else if m.status === 'year-mismatch'}
								<span class="chip chip-warning">
									{t('refs.yearArrow', { from: m.citation.year ?? '—', to: m.matched?.year ?? '—' })}
								</span>
							{:else}
								<span class="chip chip-warning">{t('refs.status.author')}</span>
							{/if}
						</div>
						{#if m.candidates.length}
							<p class="small muted">
								{t('refs.similar')}
								{m.candidates.map((c) => `${c.authors[0] ?? '?'} (${c.year ?? '?'})`).join(', ')}
							</p>
						{/if}
						{#if issueFor(m.citation.raw)}
							<button class="btn btn-sm" onclick={() => onjump(issueFor(m.citation.raw)!)}>
								{t('refs.showInText')}
							</button>
						{/if}
					</li>
				{/each}
			</ul>
		{/if}
	{/if}

	{#if view === 'orphans'}
		{#if r.orphans.length === 0}
			<p class="empty muted">{t('refs.emptyOrphans')}</p>
		{:else}
			<ul class="issue-list">
				{#each r.orphans as o (o.id)}
					<li class="mini card">
						<div class="mini-head">
							<span class="mono">{authorLabel(o.authors)}, {o.year ?? '—'}</span>
							<span class="chip chip-warning">{t('refs.uncited')}</span>
							<span class="conf small muted">
								{o.confidence === 'low' ? confidenceLabel('low') : ''}
							</span>
						</div>
						<p class="raw-ref">{excerpt(o.raw, 200)}</p>
					</li>
				{/each}
			</ul>
		{/if}
	{/if}

	<section class="method card">
		<h3>{t('refs.methodTitle')}</h3>
		<p class="small muted">{t('refs.methodBody')}</p>
	</section>
</div>

<style>
	.refs-tab {
		display: grid;
		gap: 0.9rem;
	}
	.tiles {
		display: grid;
		grid-template-columns: repeat(5, 1fr);
		gap: 0.6rem;
	}
	@media (max-width: 820px) {
		.tiles {
			grid-template-columns: repeat(2, 1fr);
		}
	}
	.tile {
		background: var(--card);
		border: 1px solid var(--line);
		border-radius: 11px;
		padding: 0.75rem 0.85rem;
		display: flex;
		flex-direction: column;
	}
	.tile .n {
		font-family: var(--font-display);
		font-size: 1.6rem;
		line-height: 1.1;
		font-variant-numeric: tabular-nums;
	}
	.tile .l {
		font-size: 0.71rem;
		color: var(--muted);
	}
	.notice {
		background: var(--warning-soft);
		border: 1px solid #ecd6ac;
		border-radius: 10px;
		padding: 0.8rem 1rem;
		font-size: 0.86rem;
		color: #6b4206;
	}
	.seg {
		display: inline-flex;
		background: var(--paper-2);
		border: 1px solid var(--line);
		border-radius: 9px;
		padding: 3px;
		gap: 3px;
		justify-self: start;
	}
	.seg-btn {
		border: 0;
		background: transparent;
		font-size: 0.8rem;
		padding: 0.3rem 0.65rem;
		border-radius: 6px;
		cursor: pointer;
		color: var(--ink-2);
	}
	.seg-btn.active {
		background: #fff;
		box-shadow: var(--shadow-sm);
		font-weight: 600;
		color: var(--ink);
	}
	.count {
		color: var(--muted);
		font-variant-numeric: tabular-nums;
	}
	.table-wrap {
		overflow-x: auto;
	}
	table {
		width: 100%;
		border-collapse: collapse;
		font-size: 0.83rem;
	}
	th {
		text-align: left;
		font-size: 0.68rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--muted);
		font-weight: 650;
		padding: 0.6rem 0.8rem;
		border-bottom: 1px solid var(--line);
		background: var(--paper);
	}
	td {
		padding: 0.55rem 0.8rem;
		border-bottom: 1px solid var(--line);
		vertical-align: top;
	}
	tbody tr:last-child td {
		border-bottom: 0;
	}
	.match-cell {
		display: flex;
		flex-direction: column;
		gap: 0.1rem;
	}
	.raw-ref {
		font-size: 0.76rem;
		color: var(--muted);
	}
	.issue-list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: grid;
		gap: 0.55rem;
	}
	.mini {
		padding: 0.75rem 0.9rem;
		display: grid;
		gap: 0.35rem;
		justify-items: start;
	}
	.mini.critical {
		border-left: 3px solid var(--critical);
	}
	.mini-head {
		display: flex;
		gap: 0.5rem;
		align-items: center;
		flex-wrap: wrap;
	}
	.mini p {
		margin: 0;
	}
	.empty {
		padding: 1rem 0;
	}
	.method {
		padding: 1.1rem 1.2rem;
	}
	.method h3 {
		font-family: var(--font-ui);
		font-size: 0.92rem;
		font-weight: 650;
		margin: 0 0 0.4rem;
	}
	.method p {
		margin: 0;
	}
</style>
